import parseISO from 'date-fns/parseISO'
import differenceInSeconds from 'date-fns/differenceInSeconds'
import differenceInMinutes from 'date-fns/differenceInMinutes'
import differenceInHours from 'date-fns/differenceInHours'
import differenceInDays from 'date-fns/differenceInDays'
import differenceInWeeks from 'date-fns/differenceInWeeks'
import format from 'date-fns/format'
import pluralize from 'pluralize'

/**
 * Takes an ISO date string and returns a string relative to current day based on the style guide https://www.notion.so/Sylvera-writing-style-guide-2dabfc877d054ec2a99c4aaa011f3444?pvs=4#0b38e12b3c03437ea82a3d3997339ed3.
 *
 * @param {string} date The original string date.
 * @param {string} date The current date.
 * @return {string} Formatted date relative to current day.
 */
export function getRelativeDateStrFromISO(
  date: string,
  now = new Date(),
  formatPattern: 'yyyy-MM-dd' | 'dd MMM yyyy' | 'yyyy MMM' = 'yyyy-MM-dd',
): string {
  const dateParsed = parseISO(date)
  const diffSeconds = differenceInSeconds(now, dateParsed)
  const diffMinutes = Math.abs(differenceInMinutes(now, dateParsed))
  const diffHours = Math.abs(differenceInHours(now, dateParsed))
  const diffDays = Math.abs(differenceInDays(now, dateParsed))
  const diffWeeks = Math.abs(differenceInWeeks(now, dateParsed))

  const isFuture = Math.sign(diffSeconds) === -1

  if (diffMinutes < 2 && diffMinutes >= 0) {
    if (isFuture) return `shortly`
    return `just now`
  }

  if (diffMinutes >= 2 && diffMinutes < 60) {
    if (isFuture) return `in ${diffMinutes} minutes`
    return `${diffMinutes} minutes ago`
  }

  if (diffDays === 0) {
    if (isFuture) return `in ${diffHours} ${pluralize('hour', diffHours)}`
    return `${diffHours} ${pluralize('hour', diffHours)} ago`
  }

  if (diffDays === 1) {
    if (isFuture) return `tomorrow`
    return `yesterday`
  }

  if (diffWeeks === 0) {
    if (isFuture) return `in ${diffDays} ${pluralize('day', diffDays)}`
    return `${diffDays} ${pluralize('day', diffDays)} ago`
  }

  if (diffDays > 7) {
    return format(dateParsed, formatPattern)
  }

  if (diffWeeks === 1) {
    if (isFuture) return `in 1 week`
    return '1 week ago'
  }

  return format(dateParsed, formatPattern)
}
